import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Slide,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  Add as AddIcon,
  PhoneAndroid as AndroidIcon,
  PhoneIphone as IphoneIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { detectDeviceType, isMobileDevice } from '../utils/deviceDetection';
import { useTheme } from '../contexts/ThemeContext';
import { getMm } from "../theme/modernMaterialEnterprise";

const STORAGE_KEY = 'homeScreenPopupDismissed';

const AddToHomeScreenPopup = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [show, setShow] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [deviceType, setDeviceType] = useState('other');

  useEffect(() => {
    // Only show on mobile devices
    if (!isMobileDevice()) {
      return;
    }

    // Check if user has dismissed this before
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === 'true') {
      return;
    }

    // Show the popup
    setShow(true);
    setDeviceType(detectDeviceType());
  }, []);

  const handleDismiss = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    setShow(false);
  };

  if (!show) {
    return null;
  }

  // Only show for iPhone or Android
  if (deviceType === 'other') {
    return null;
  }

  const isiPhone = deviceType === 'iphone';

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          p: 2,
          pb: { xs: 2, sm: 3 },
          pointerEvents: 'none',
        }}
      >
        <Card
          sx={{
            pointerEvents: 'auto',
            borderRadius: '16px 16px 0 0',
            boxShadow: isDarkMode
              ? '0 -8px 32px rgba(0,0,0,0.5)'
              : '0 -8px 32px rgba(0,0,0,0.2)',
            border: isDarkMode
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid #e9ecef',
            borderBottom: 'none',
            background: isDarkMode
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            maxWidth: '600px',
            mx: 'auto',
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {isiPhone ? (
                  <IphoneIcon
                    sx={{
                      fontSize: 32,
                      color: getMm(isDarkMode).primary,
                    }}
                  />
                ) : (
                  <AndroidIcon
                    sx={{
                      fontSize: 32,
                      color: getMm(isDarkMode).primary,
                    }}
                  />
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: getMm(isDarkMode).primary,
                    fontSize: '1.1rem',
                  }}
                >
                  {t('add_to_home_screen_title')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleDismiss}
                sx={{
                  color: isDarkMode ? '#b0b0b0' : '#7f8c8d',
                  '&:hover': {
                    backgroundColor: getMm(isDarkMode).hoverWash,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? '#b0b0b0' : '#666666',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              {t('add_to_home_screen_description')}
            </Typography>

            {/* Instructions */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: '12px',
                backgroundColor: isDarkMode
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.03)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: getMm(isDarkMode).primary,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {isiPhone ? (
                  <>
                    <IphoneIcon sx={{ fontSize: 20 }} />
                    {t('iphone_instructions')}
                  </>
                ) : (
                  <>
                    <AndroidIcon sx={{ fontSize: 20 }} />
                    {t('android_instructions')}
                  </>
                )}
              </Typography>

              {/* iPhone Steps */}
              {isiPhone && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      1
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <ShareIcon sx={{ fontSize: 18 }} />
                        {t('iphone_step1')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      2
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 18 }} />
                        {t('iphone_step2')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      3
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <HomeIcon sx={{ fontSize: 18 }} />
                        {t('iphone_step3')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Android Steps */}
              {!isiPhone && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      1
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                        }}
                      >
                        {t('android_step1')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      2
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 18 }} />
                        {t('android_step2')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: getMm(isDarkMode).primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      3
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#e0e0e0' : '#495057',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <HomeIcon sx={{ fontSize: 18 }} />
                        {t('android_step3')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Don't show again checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#b0b0b0' : '#7f8c8d',
                    '&.Mui-checked': {
                      color: getMm(isDarkMode).primary,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#b0b0b0' : '#7f8c8d',
                    fontSize: '0.85rem',
                  }}
                >
                  {t('dont_show_again')}
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            {/* Got it button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleDismiss}
              sx={{
                background: isDarkMode
                  ? 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)'
                  : 'linear-gradient(45deg, #2c3e50 30%, #34495e 90%)',
                color: 'white',
                borderRadius: '12px',
                py: 1.25,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: isDarkMode
                  ? '0 4px 16px rgba(25, 118, 210, 0.3)'
                  : '0 4px 16px rgba(44, 62, 80, 0.3)',
                '&:hover': {
                  background: isDarkMode
                    ? 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)'
                    : 'linear-gradient(45deg, #34495e 30%, #2c3e50 90%)',
                  transform: 'translateY(-1px)',
                  boxShadow: isDarkMode
                    ? '0 8px 24px rgba(25, 118, 210, 0.4)'
                    : '0 8px 24px rgba(44, 62, 80, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('got_it')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Slide>
  );
};

export default AddToHomeScreenPopup;

